/**
 * Tariff Descriptions Database
 *
 * Comprehensive descriptions and metadata for ALL 9 Polish energy tariff types.
 * Provides detailed information about tariff characteristics, availability,
 * use cases, and visual styling for UI components.
 *
 * @see /frontend/src/utils/tariffMetadata.ts - Helper functions and metadata
 * @see /frontend/src/types/graphql.ts - TariffType enum
 * @see /frontend/src/utils/osdMetadata.ts - OSD availability mapping
 */

import { TariffType } from '../../types/graphql';
import type { OSDCode } from '../../utils/osdMetadata';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Complete tariff description with all metadata
 */
export interface TariffDescription {
  /** Tariff code (e.g., 'G11', 'G12') */
  code: TariffType;

  /** Polish display name */
  name: string;

  /** Short one-sentence description */
  shortDescription: string;

  /** Full 2-3 sentence description */
  fullDescription: string;

  /** Array of use cases (best suited for) */
  bestFor: string[];

  /** Array of situations where NOT recommended */
  notFor: string[];

  /** OSD operators offering this tariff */
  availableOSDs: OSDCode[];

  /** Requires smart meter installation */
  requiresSmartMeter: boolean;

  /** Requires hourly RCE price data (G14 only) */
  requiresPriceData?: boolean;

  /** Badge color for UI display */
  color: string;

  /** Tabler icon name */
  icon: string;

  /** Optional badge text (e.g., 'NOWOŚĆ', 'ANTYSMOG') */
  badge?: string;

  /** Time zone configuration summary */
  timeZoneInfo: string;

  /** Estimated savings potential */
  estimatedSavings: string;
}

// ============================================================================
// TARIFF DESCRIPTIONS DATABASE
// ============================================================================

/**
 * Complete Tariff Descriptions Registry
 * All 9 supported Polish energy tariffs with full metadata
 */
export const TARIFF_DESCRIPTIONS: Record<TariffType, TariffDescription> = {
  // ==========================================================================
  // G11 - Single Rate (All OSDs)
  // ==========================================================================
  [TariffType.G11]: {
    code: TariffType.G11,
    name: 'G11 - Taryfa jednostrefowa',
    shortDescription: 'Stała cena energii przez całą dobę',
    fullDescription:
      'Taryfa G11 to najprostsza taryfa energetyczna z jedną stałą stawką przez całą dobę. ' +
      'Nie wymaga żadnych zmian w spobie użytkowania energii i jest idealna dla gospodarstw ' +
      'domowych z równomiernym zużyciem. Brak skomplikowanych stref czasowych - płacisz tyle samo ' +
      'za każdą kilowatogodzinę, niezależnie od pory dnia.',
    bestFor: [
      'Równomierne zużycie energii przez całą dobę',
      'Małe mieszkania (1-2 osoby) bez dużych odbiorników',
      'Brak możliwości przesunięcia zużycia na godziny nocne',
      'Gospodarstwa bez elastyczności czasowej',
      'Osoby ceniące prostotę rozliczeń',
    ],
    notFor: [
      'Gospodarstwa z możliwością nocnego prania/zmywania',
      'Właściciele pomp ciepła z zasobnikami',
      'Posiadacze samochodów elektrycznych',
      'Gospodarstwa z elastycznym harmonogramem pracy',
      'Osoby szukające aktywnych oszczędności',
    ],
    availableOSDs: ['TAURON', 'PGE', 'ENEA', 'ENERGA'],
    requiresSmartMeter: false,
    color: 'gray',
    icon: 'IconClock',
    timeZoneInfo: '24h - jedna stawka przez cały dzień i noc',
    estimatedSavings: 'Brak oszczędności (taryfa referencyjna)',
  },

  // ==========================================================================
  // G12 - Day/Night (All OSDs)
  // ==========================================================================
  [TariffType.G12]: {
    code: TariffType.G12,
    name: 'G12 - Taryfa dwustrefowa',
    shortDescription: 'Tańsza energia w nocy (22:00-06:00)',
    fullDescription:
      'Taryfa G12 dzieli dobę na dwie strefy czasowe: dzień (06:00-22:00) z wyższą stawką ' +
      'oraz noc (22:00-06:00) z niższą stawką. Aby była opłacalna, należy przesunąć co najmniej ' +
      '30-40% zużycia na godziny nocne. Idealna dla osób z elastycznym harmonogramem domowych ' +
      'prac (pranie, zmywanie) lub ładowaniem samochodu elektrycznego w nocy.',
    bestFor: [
      'Nocne pranie, zmywanie (po 22:00)',
      'Ładowanie samochodu elektrycznego w nocy',
      'Pompy ciepła z zasobnikami ciepła',
      'Gospodarstwa 3-4 osobowe z elastycznością czasową',
      'Możliwość przesunięcia 30-40% zużycia na noc',
    ],
    notFor: [
      'Brak możliwości nocnego korzystania z urządzeń',
      'Małe zużycie nocne (<30% całości)',
      'Praca na nocną zmianę (wysokie zużycie w dzień)',
      'Gospodarstwa bez programowalnych urządzeń',
      'Małe mieszkania z niskim zużyciem całkowitym',
    ],
    availableOSDs: ['TAURON', 'PGE', 'ENEA', 'ENERGA'],
    requiresSmartMeter: false,
    color: 'blue',
    icon: 'IconMoon',
    timeZoneInfo: 'Dzień: 06:00-22:00 (16h) | Noc: 22:00-06:00 (8h)',
    estimatedSavings: 'Do 20% przy >40% zużycia nocnego',
  },

  // ==========================================================================
  // G12w - Weekend (TAURON, PGE, ENEA, ENERGA)
  // ==========================================================================
  [TariffType.G12w]: {
    code: TariffType.G12w,
    name: 'G12w - Taryfa weekendowa',
    shortDescription: 'Tańsza energia w weekendy i święta',
    fullDescription:
      'Taryfa G12w łączy zróżnicowanie czasowe dni roboczych (dzień/noc) z całodobową niską ' +
      'stawką w weekendy i święta. Idealna dla osób pracujących standardowo w tygodniu i ' +
      'przesuwających główne zużycie energii (pranie, zmywanie, gotowanie) na soboty i niedziele. ' +
      'Święta państwowe również objęte całodzienną niską stawką.',
    bestFor: [
      'Praca w tygodniu, aktywność domowa w weekendy',
      'Pranie i zmywanie w soboty/niedziele',
      'Home office w weekendy',
      'Duża rodzina skupiająca prace domowe na weekendy',
      'Możliwość przesunięcia 50%+ zużycia na weekendy',
    ],
    notFor: [
      'Praca w weekendy (wysokie zużycie w dni robocze)',
      'Równomierne zużycie każdego dnia tygodnia',
      'Brak możliwości odkładania prac domowych',
      'Nieregularny rozkład czasu pracy (zmianowy)',
      'Małe zużycie weekendowe',
    ],
    availableOSDs: ['TAURON', 'PGE', 'ENEA', 'ENERGA'],
    requiresSmartMeter: false,
    color: 'teal',
    icon: 'IconCalendarEvent',
    timeZoneInfo:
      'Dni robocze: Dzień 06:00-22:00 | Noc 22:00-06:00\n' +
      'Weekendy i święta: Całodobowa niska stawka',
    estimatedSavings: 'Do 25% przy >50% zużycia weekendowego',
  },

  // ==========================================================================
  // G12as - Anti-smog (TAURON, ENEA, ENERGA)
  // ==========================================================================
  [TariffType.G12as]: {
    code: TariffType.G12as,
    name: 'G12as - Taryfa antysmogowa',
    shortDescription: 'Preferencyjna taryfa nocna dla ogrzewania elektrycznego',
    fullDescription:
      'Taryfa G12as to specjalna taryfa dla gospodarstw z całorocznym ogrzewaniem elektrycznym ' +
      '(pompy ciepła, akumulacyjne piece elektryczne). Oferuje bardzo korzystne stawki nocne, ' +
      'zachęcając do gromadzenia ciepła w zasobnikach w nocy i wykorzystywania go w dzień. ' +
      'Wymaga deklaracji ogrzewania elektrycznego i wysokiej mocy zainstalowanej (zwykle >10 kW).',
    bestFor: [
      'Pompy ciepła jako główne źródło ogrzewania',
      'Piece akumulacyjne elektryczne',
      'Ogrzewanie elektryczne całoroczne',
      'Wysoka moc zainstalowana (>10 kW)',
      'Zasobnik ciepła z nocnym ładowaniem',
    ],
    notFor: [
      'Brak ogrzewania elektrycznego',
      'Ogrzewanie gazowe lub węglowe',
      'Niska moc instalacji (<5 kW)',
      'Sezonowe użytkowanie ogrzewania',
      'Małe mieszkania bez pompy ciepła',
    ],
    availableOSDs: ['TAURON', 'ENEA', 'ENERGA'],
    requiresSmartMeter: false,
    color: 'green',
    icon: 'IconLeaf',
    badge: 'Antysmog',
    timeZoneInfo: 'Dzień: 06:00-22:00 | Noc: 22:00-06:00 (całoroczna)',
    estimatedSavings: 'Do 30% przy ogrzewaniu elektrycznym',
  },

  // ==========================================================================
  // G12n - Night/Sunday (PGE only)
  // ==========================================================================
  [TariffType.G12n]: {
    code: TariffType.G12n,
    name: 'G12n - Taryfa nocna (PGE)',
    shortDescription: 'Tania noc 01:00-05:00 dni robocze + całe weekendy',
    fullDescription:
      'Taryfa G12n to specyficzna oferta PGE Dystrybucja z bardzo krótką (4-godzinną) strefą ' +
      'nocną w dni robocze (01:00-05:00), ale z całodobową tanią strefą w weekendy i święta. ' +
      'Idealna dla osób z elastycznym zużyciem weekendowym i możliwością nocnego ładowania ' +
      'samochodu elektrycznego lub programowania urządzeń na godziny 01:00-05:00.',
    bestFor: [
      'Ładowanie samochodu elektrycznego 01:00-05:00',
      'Programowalne urządzenia (pralka z odroczoniem startem)',
      'Duże zużycie weekendowe',
      'Elastyczny harmonogram domowych prac',
      'Klienci PGE Dystrybucja',
    ],
    notFor: [
      'Brak możliwości nocnego korzystania (01:00-05:00)',
      'Małe zużycie weekendowe',
      'Klienci innych OSD (TAURON, ENEA, ENERGA)',
      'Standardowy harmonogram nocny (22:00-06:00)',
      'Brak programowalnych urządzeń',
    ],
    availableOSDs: ['PGE'],
    requiresSmartMeter: false,
    color: 'indigo',
    icon: 'IconMoonStars',
    badge: 'PGE',
    timeZoneInfo:
      'Dni robocze: Droga strefa 05:00-01:00 | Tania noc 01:00-05:00\n' +
      'Weekendy i święta: Całodobowa tania strefa',
    estimatedSavings: 'Do 20% przy intensywnym zużyciu weekendowym',
  },

  // ==========================================================================
  // G12r - Extended Valley (ENERGA only)
  // ==========================================================================
  [TariffType.G12r]: {
    code: TariffType.G12r,
    name: 'G12r - Rozszerzona weekendowa (Energa)',
    shortDescription: 'Dłuższa noc 21:00-07:00 + całe weekendy',
    fullDescription:
      'Taryfa G12r to oferta Energa-Operator z dłuższą strefą off-peak (21:00-07:00 w dni ' +
      'robocze, 10 godzin) oraz całodobową tanią strefą w weekendy i święta. Bardziej elastyczna ' +
      'niż standardowa G12w, pozwala na wieczorne korzystanie z energii (po 21:00) oraz poranne ' +
      '(do 07:00) w niskiej stawce. Idealna dla osób z późnymi godzinami aktywności.',
    bestFor: [
      'Wieczorne i poranne zużycie (21:00-07:00)',
      'Ładowanie samochodu elektrycznego wieczorem',
      'Późne pranie/zmywanie (po 21:00)',
      'Wczesne poranne gotowanie/pranie (przed 07:00)',
      'Klienci Energa-Operator',
    ],
    notFor: [
      'Brak możliwości wieczornego/porannego zużycia',
      'Standardowy harmonogram 22:00-06:00 (użyj G12)',
      'Klienci innych OSD (TAURON, PGE, ENEA)',
      'Małe zużycie w strefie off-peak',
      'Preferowanie nocnych godzin (22:00-06:00)',
    ],
    availableOSDs: ['ENERGA'],
    requiresSmartMeter: false,
    color: 'orange',
    icon: 'IconSunset2',
    badge: 'ENERGA',
    timeZoneInfo:
      'Dni robocze: Peak 07:00-21:00 | Off-peak 21:00-07:00\n' +
      'Weekendy i święta: Całodobowa off-peak strefa',
    estimatedSavings: 'Do 25% przy wieczornym/porannym zużyciu',
  },

  // ==========================================================================
  // G13 - Three-zone Seasonal (TAURON only)
  // ==========================================================================
  [TariffType.G13]: {
    code: TariffType.G13,
    name: 'G13 - Taryfa trzystrefowa sezonowa',
    shortDescription: 'Szczyty poranne/wieczorne, poza szczytem, noc',
    fullDescription:
      'Taryfa G13 dzieli dobę na trzy strefy: szczyt (poranny 7-9 i wieczorny 17-21 w sezonie ' +
      'zimowym), poza szczytem (dzień poza godzinami szczytu + całe weekendy) oraz noc (22:00-07:00). ' +
      'Wymaga aktywnego zarządzania zużyciem, aby unikać drogich godzin szczytowych. Idealna dla ' +
      'prosumentów z bateriami domowymi lub systemami zarządzania energią (HEMS).',
    bestFor: [
      'Unikanie szczytów energetycznych (7-9, 17-21)',
      'Prosumenci z baterią domową',
      'Systemy zarządzania energią (HEMS)',
      'Automaty czasowe na urządzeniach',
      'Elastyczne gospodarstwa z planowaniem zużycia',
    ],
    notFor: [
      'Brak możliwości unikania szczytów',
      'Standardowe godziny gotowania (17:00-21:00)',
      'Małe gospodarstwa bez automatyzacji',
      'Nieelastyczny harmonogram pracy',
      'Klienci innych OSD (PGE, ENEA, ENERGA)',
    ],
    availableOSDs: ['TAURON'],
    requiresSmartMeter: false,
    color: 'violet',
    icon: 'IconChartLine',
    timeZoneInfo:
      'Szczyt: 07:00-09:00, 17:00-21:00 (sezon zimowy)\n' +
      'Poza szczytem: pozostałe godziny dzienne + weekendy\n' +
      'Noc: 22:00-07:00',
    estimatedSavings: 'Do 35% przy aktywnym unikaniu godzin szczytowych',
  },

  // ==========================================================================
  // G13s - Three-zone Auto/OZE (TAURON only)
  // ==========================================================================
  [TariffType.G13s]: {
    code: TariffType.G13s,
    name: 'G13s - Taryfa dynamiczna OZE',
    shortDescription: 'Najtańsza w godzinach produkcji OZE (9-17 lato)',
    fullDescription:
      'Taryfa G13s to najbardziej zaawansowana taryfa dopasowana do wzrostu produkcji energii ' +
      'odnawialnej w Polsce. Oferuje najniższe stawki w godzinach produkcji słonecznej (9-17 w ' +
      'sezonie letnim IV-IX), zachęcając do wykorzystywania czystej energii. Wymaga inteligentnego ' +
      'licznika oraz systemu zarządzania energią lub baterii do optymalizacji zużycia.',
    bestFor: [
      'Fotowoltaika z magazynem energii (bateria domowa)',
      'Prosumenci z systemem zarządzania (HEMS)',
      'Ładowanie samochodu elektrycznego w dzień (home office)',
      'Pompy ciepła z inteligentnym sterowaniem',
      'Gospodarstwa ze smart home automation',
    ],
    notFor: [
      'Brak fotowoltaiki lub baterii',
      'Brak inteligentnego licznika',
      'Standardowy harmonogram pracy (zużycie wieczorem)',
      'Małe gospodarstwa bez automatyzacji',
      'Klienci innych OSD (PGE, ENEA, ENERGA)',
    ],
    availableOSDs: ['TAURON'],
    requiresSmartMeter: true,
    color: 'lime',
    icon: 'IconSolarPanel',
    badge: 'NOWOŚĆ VII 2025',
    timeZoneInfo:
      'Lato (IV-IX): Tania strefa 09:00-17:00 (produkcja solarna)\n' +
      'Zima (X-III): Szczyt 07:00-09:00, 17:00-21:00\n' +
      'Noc: 22:00-07:00 (całoroczna)',
    estimatedSavings: 'Do 40% przy fotowoltaice + baterii',
  },

  // ==========================================================================
  // G14 - Dynamic Hourly RCE (TAURON only)
  // ==========================================================================
  [TariffType.G14]: {
    code: TariffType.G14,
    name: 'G14 - Dynamiczna RCE',
    shortDescription: 'Taryfa z dynamicznymi cenami godzinowymi opartymi na rynkowej cenie energii (RCE)',
    fullDescription:
      'Taryfa G14 oferuje ceny energii zmieniające się co godzinę na podstawie Rynkowej Ceny ' +
      'Energii (RCE) z PSE. Opłaty dystrybucyjne bazują na średniej stawce z taryfy G11. Idealna ' +
      'dla użytkowników z systemami automatyzacji smart home, którzy mogą przesuwać zużycie na ' +
      'godziny z niskimi cenami RCE (często w nocy lub w weekendy). Wymaga inteligentnego licznika ' +
      'oraz codziennego monitorowania prognoz cen.',
    bestFor: [
      'Zaawansowana automatyzacja smart home',
      'Prosumenci z magazynem energii i sterowaniem',
      'Pompy ciepła z predykcyjnym sterowaniem',
      'Ładowanie EV z optymalizacją kosztów',
      'Gospodarstwa z elastycznym zużyciem 24/7',
    ],
    notFor: [
      'Brak automatyzacji i systemu zarządzania',
      'Brak inteligentnego licznika',
      'Nieelastyczny harmonogram zużycia',
      'Osoby preferujące przewidywalne rachunki',
      'Klienci innych OSD (PGE, ENEA, ENERGA)',
    ],
    availableOSDs: ['TAURON'],
    requiresSmartMeter: true,
    requiresPriceData: true,
    color: 'red',
    icon: 'IconActivity',
    badge: 'NOWOŚĆ 2025',
    timeZoneInfo: 'Ceny godzinowe 0-23h, publikowane z jednodniowym wyprzedzeniem (D-1)',
    estimatedSavings: 'Do 30% przy optymalizacji zużycia na podstawie prognoz RCE',
  },

  // ==========================================================================
  // BUSINESS TARIFFS (C-series) - Small Business (C1 ≤40kW)
  // ==========================================================================
  [TariffType.C11]: {
    code: TariffType.C11,
    name: 'C11 - Jednostrefowa firma',
    shortDescription: 'Stała cena energii 24h dla małych firm (≤40 kW)',
    fullDescription:
      'Taryfa C11 to podstawowa taryfa biznesowa z jedną stałą stawką przez całą dobę. ' +
      'Przeznaczona dla małych firm z mocą umowną do 40 kW. Najprostsza w rozliczeniach, ' +
      'idealna dla firm z równomiernym zużyciem energii. Zawiera opłatę mocową naliczaną ' +
      'od mocy umownej (kW), co odróżnia ją od taryf gospodarczych.',
    bestFor: [
      'Małe firmy z równomiernym zużyciem',
      'Biura pracujące w godzinach dziennych',
      'Sklepy i punkty usługowe',
      'Małe warsztaty i zakłady rzemieślnicze',
      'Firmy bez możliwości przesunięcia zużycia na noc',
    ],
    notFor: [
      'Firmy z wysokim zużyciem nocnym',
      'Zakłady z przerwami w produkcji',
      'Firmy powyżej 40 kW mocy umownej',
      'Przedsiębiorstwa szukające aktywnych oszczędności',
    ],
    availableOSDs: ['TAURON', 'PGE', 'ENEA', 'ENERGA'],
    requiresSmartMeter: false,
    color: 'blue',
    icon: 'IconBuildingStore',
    badge: 'FIRMA',
    timeZoneInfo: '24h - jedna stawka przez cały dzień i noc',
    estimatedSavings: 'Referencyjna dla firm (brak oszczędności)',
  },

  [TariffType.C12a]: {
    code: TariffType.C12a,
    name: 'C12a - Dwustrefowa firma',
    shortDescription: 'Tańsza energia w nocy (22:00-06:00) dla małych firm',
    fullDescription:
      'Taryfa C12a to dwustrefowa taryfa biznesowa dzieląca dobę na dzień (06:00-22:00) ' +
      'z wyższą stawką oraz noc (22:00-06:00) z niższą stawką. Idealna dla firm mogących ' +
      'przesunąć część zużycia na godziny nocne. Moc umowna do 40 kW. Oszczędności przy ' +
      'minimum 30-40% zużycia w strefie nocnej.',
    bestFor: [
      'Magazyny z nocnym ładowaniem',
      'Piekarnie z nocną produkcją',
      'Firmy z elastycznym harmonogramem pracy',
      'Zakłady z możliwością nocnej pracy urządzeń',
      'Firmy z chłodniami i mroźniami',
    ],
    notFor: [
      'Biura pracujące tylko w dzień',
      'Firmy bez możliwości pracy nocnej',
      'Sklepy otwarte 10-20',
      'Przedsiębiorstwa z niskim zużyciem nocnym',
    ],
    availableOSDs: ['TAURON', 'PGE', 'ENEA', 'ENERGA'],
    requiresSmartMeter: false,
    color: 'cyan',
    icon: 'IconMoon',
    badge: 'FIRMA',
    timeZoneInfo: 'Dzień: 06:00-22:00 (16h) | Noc: 22:00-06:00 (8h)',
    estimatedSavings: 'Do 15-20% przy >40% zużycia nocnego',
  },

  [TariffType.C12b]: {
    code: TariffType.C12b,
    name: 'C12b - Dwustrefowa sezonowa firma',
    shortDescription: 'Tańsza energia w nocy z wariacją sezonową dla małych firm',
    fullDescription:
      'Taryfa C12b to dwustrefowa taryfa biznesowa z różnicowaniem sezonowym. ' +
      'W sezonie zimowym (X-III) stawki są wyższe, w letnim (IV-IX) niższe. Idealna dla ' +
      'firm z sezonowym profilem działalności. Moc umowna do 40 kW. Pozwala na większe ' +
      'oszczędności latem przy intensywnym zużyciu.',
    bestFor: [
      'Firmy z sezonową działalnością',
      'Rolnictwo i przetwórstwo rolne',
      'Branża turystyczna (hotele, pensjonaty)',
      'Firmy z klimatyzacją latem',
      'Zakłady z wyższym zużyciem letnim',
    ],
    notFor: [
      'Firmy z równomiernym zużyciem całorocznym',
      'Zakłady z dominującym zużyciem zimowym',
      'Biura bez klimatyzacji',
      'Przedsiębiorstwa preferujące stałe stawki',
    ],
    availableOSDs: ['TAURON', 'PGE', 'ENEA', 'ENERGA'],
    requiresSmartMeter: false,
    color: 'teal',
    icon: 'IconSun',
    badge: 'FIRMA',
    timeZoneInfo: 'Zima (X-III): wyższe stawki | Lato (IV-IX): niższe stawki',
    estimatedSavings: 'Do 10-15% dla firm z sezonowym profilem',
  },

  // ==========================================================================
  // BUSINESS TARIFFS (C-series) - Medium/Large Business (C2 >40kW)
  // ==========================================================================
  [TariffType.C21]: {
    code: TariffType.C21,
    name: 'C21 - Jednostrefowa duża firma',
    shortDescription: 'Stała cena 24h dla średnich i dużych firm (>40 kW)',
    fullDescription:
      'Taryfa C21 to podstawowa taryfa dla średnich i dużych przedsiębiorstw z mocą ' +
      'umowną powyżej 40 kW. Jedna stała stawka przez całą dobę, prosta w rozliczeniach. ' +
      'Opłata mocowa stanowi znaczącą część rachunku - płatna od mocy umownej (kW). ' +
      'Idealna dla firm z równomiernym profilem zużycia energii.',
    bestFor: [
      'Zakłady produkcyjne z ciągłą pracą',
      'Centra logistyczne i magazyny',
      'Duże biurowce i obiekty komercyjne',
      'Fabryki z równomiernym obciążeniem',
      'Obiekty handlowe wielkopowierzchniowe',
    ],
    notFor: [
      'Firmy poniżej 40 kW (użyj C11)',
      'Zakłady z możliwością pracy nocnej',
      'Przedsiębiorstwa z sezonowym profilem',
      'Firmy szukające aktywnej optymalizacji kosztów',
    ],
    availableOSDs: ['TAURON', 'PGE', 'ENEA', 'ENERGA'],
    requiresSmartMeter: false,
    color: 'indigo',
    icon: 'IconBuilding',
    badge: 'FIRMA',
    timeZoneInfo: '24h - jedna stawka przez cały dzień i noc',
    estimatedSavings: 'Referencyjna dla dużych firm',
  },

  [TariffType.C22a]: {
    code: TariffType.C22a,
    name: 'C22a - Dwustrefowa duża firma',
    shortDescription: 'Dzień/noc dla średnich i dużych firm (>40 kW)',
    fullDescription:
      'Taryfa C22a to dwustrefowa taryfa dla dużych przedsiębiorstw z mocą powyżej 40 kW. ' +
      'Dzieli dobę na strefę dzienną (wyższa stawka) i nocną (niższa stawka). Wymaga ' +
      'możliwości przesunięcia znaczącej części zużycia na godziny nocne. Oszczędności ' +
      'możliwe przy minimum 35-40% zużycia w strefie nocnej.',
    bestFor: [
      'Zakłady z nocną produkcją',
      'Huty i przemysł ciężki',
      'Centra danych z elastycznym obciążeniem',
      'Duże chłodnie i mroźnie',
      'Firmy z automatyzacją procesów nocnych',
    ],
    notFor: [
      'Zakłady pracujące tylko w dzień',
      'Biura i centra usługowe',
      'Firmy bez możliwości pracy 24/7',
      'Przedsiębiorstwa z niskim zużyciem nocnym',
    ],
    availableOSDs: ['TAURON', 'PGE', 'ENEA', 'ENERGA'],
    requiresSmartMeter: false,
    color: 'violet',
    icon: 'IconBuildingFactory2',
    badge: 'FIRMA',
    timeZoneInfo: 'Dzień: 06:00-22:00 | Noc: 22:00-06:00',
    estimatedSavings: 'Do 20% przy >40% zużycia nocnego',
  },

  [TariffType.C22b]: {
    code: TariffType.C22b,
    name: 'C22b - Dwustrefowa sezonowa duża firma',
    shortDescription: 'Dzień/noc z wariacją sezonową dla dużych firm (>40 kW)',
    fullDescription:
      'Taryfa C22b to najbardziej zaawansowana taryfa dla dużych przedsiębiorstw. ' +
      'Łączy różnicowanie dzień/noc z sezonowym (zima/lato). Pozwala na maksymalne ' +
      'oszczędności przy odpowiednim zarządzaniu zużyciem. Idealna dla firm z ' +
      'elastycznym profilem produkcji i możliwością planowania.',
    bestFor: [
      'Duże zakłady z planowaniem produkcji',
      'Przemysł z sezonowym profilem',
      'Firmy z własnym działem zarządzania energią',
      'Przedsiębiorstwa z automatyzacją i optymalizacją',
      'Zakłady z magazynami energii lub UPS',
    ],
    notFor: [
      'Firmy bez działu zarządzania energią',
      'Zakłady z równomiernym profilem całorocznym',
      'Przedsiębiorstwa preferujące proste rozliczenia',
      'Firmy bez możliwości planowania zużycia',
    ],
    availableOSDs: ['TAURON', 'PGE', 'ENEA', 'ENERGA'],
    requiresSmartMeter: false,
    color: 'grape',
    icon: 'IconChartAreaLine',
    badge: 'FIRMA',
    timeZoneInfo: 'Zima (X-III): wyższe stawki | Lato (IV-IX): niższe | Dzień/Noc',
    estimatedSavings: 'Do 25% przy optymalnym zarządzaniu',
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get Tariff Description
 * Retrieve complete description for a specific tariff
 *
 * @param tariffCode - Tariff code (e.g., 'G11', 'G12')
 * @returns Complete tariff description
 */
export function getTariffDescription(tariffCode: TariffType): TariffDescription {
  return TARIFF_DESCRIPTIONS[tariffCode];
}

/**
 * Get Tariffs for OSD
 * Filter tariffs available from a specific OSD operator
 *
 * @param osdCode - OSD code (e.g., 'TAURON', 'PGE')
 * @returns Array of tariff descriptions available for this OSD
 */
export function getTariffsForOSD(osdCode: OSDCode): TariffDescription[] {
  return Object.values(TARIFF_DESCRIPTIONS).filter((tariff) =>
    tariff.availableOSDs.includes(osdCode)
  );
}

/**
 * Get Tariff Color
 * Get UI badge color for a tariff
 *
 * @param tariffCode - Tariff code
 * @returns Mantine color name
 */
export function getTariffColor(tariffCode: TariffType): string {
  return TARIFF_DESCRIPTIONS[tariffCode].color;
}

/**
 * Get Tariff Icon
 * Get Tabler icon name for a tariff
 *
 * @param tariffCode - Tariff code
 * @returns Tabler icon name (e.g., 'IconClock')
 */
export function getTariffIcon(tariffCode: TariffType): string {
  return TARIFF_DESCRIPTIONS[tariffCode].icon;
}

/**
 * Get Tariff Badge
 * Get optional badge text for a tariff (e.g., 'NOWOŚĆ', 'ANTYSMOG')
 *
 * @param tariffCode - Tariff code
 * @returns Badge text or undefined
 */
export function getTariffBadge(tariffCode: TariffType): string | undefined {
  return TARIFF_DESCRIPTIONS[tariffCode].badge;
}

/**
 * Get Smart Meter Required Tariffs
 * Get array of tariffs that require a smart meter
 *
 * @returns Array of tariff codes requiring smart meter
 */
export function getSmartMeterRequiredTariffs(): TariffType[] {
  return Object.values(TARIFF_DESCRIPTIONS)
    .filter((tariff) => tariff.requiresSmartMeter)
    .map((tariff) => tariff.code);
}

/**
 * Check if Tariff Requires Smart Meter
 * Check if a specific tariff requires smart meter installation
 *
 * @param tariffCode - Tariff code
 * @returns True if smart meter required
 */
export function requiresSmartMeter(tariffCode: TariffType): boolean {
  return TARIFF_DESCRIPTIONS[tariffCode].requiresSmartMeter;
}

/**
 * Check if Tariff Requires Price Data
 * Check if a specific tariff requires hourly RCE price data (G14)
 *
 * @param tariffCode - Tariff code
 * @returns True if price data required
 */
export function requiresPriceData(tariffCode: TariffType): boolean {
  return TARIFF_DESCRIPTIONS[tariffCode].requiresPriceData || false;
}

/**
 * Get All Tariff Codes
 * Get array of all supported tariff codes
 *
 * @returns Array of TariffType codes
 */
export function getAllTariffCodes(): TariffType[] {
  return Object.keys(TARIFF_DESCRIPTIONS) as TariffType[];
}

/**
 * Get Tariffs by Smart Meter Requirement
 * Filter tariffs by smart meter requirement
 *
 * @param requiresSmartMeter - True to get tariffs requiring smart meter, false otherwise
 * @returns Array of tariff descriptions
 */
export function getTariffsBySmartMeterRequirement(
  requiresSmartMeter: boolean
): TariffDescription[] {
  return Object.values(TARIFF_DESCRIPTIONS).filter(
    (tariff) => tariff.requiresSmartMeter === requiresSmartMeter
  );
}

/**
 * Search Tariffs
 * Search tariffs by name or description
 *
 * @param searchQuery - Search query string
 * @returns Array of matching tariff descriptions
 */
export function searchTariffs(searchQuery: string): TariffDescription[] {
  const query = searchQuery.toLowerCase();
  return Object.values(TARIFF_DESCRIPTIONS).filter(
    (tariff) =>
      tariff.name.toLowerCase().includes(query) ||
      tariff.shortDescription.toLowerCase().includes(query) ||
      tariff.fullDescription.toLowerCase().includes(query) ||
      tariff.bestFor.some((use) => use.toLowerCase().includes(query)) ||
      tariff.badge?.toLowerCase().includes(query)
  );
}

/**
 * Get OSD-Specific Tariffs
 * Get tariffs that are exclusive to a specific OSD (not available elsewhere)
 *
 * @param osdCode - OSD code
 * @returns Array of exclusive tariff descriptions
 */
export function getOSDExclusiveTariffs(osdCode: OSDCode): TariffDescription[] {
  return Object.values(TARIFF_DESCRIPTIONS).filter(
    (tariff) => tariff.availableOSDs.length === 1 && tariff.availableOSDs[0] === osdCode
  );
}

/**
 * Get Universal Tariffs
 * Get tariffs available from all OSDs
 *
 * @returns Array of universal tariff descriptions
 */
export function getUniversalTariffs(): TariffDescription[] {
  return Object.values(TARIFF_DESCRIPTIONS).filter(
    (tariff) => tariff.availableOSDs.length === 4
  );
}

/**
 * Compare Tariff Availability
 * Check if two tariffs are available from the same OSDs
 *
 * @param tariff1 - First tariff code
 * @param tariff2 - Second tariff code
 * @returns True if both available from same OSDs
 */
export function haveSameOSDAvailability(tariff1: TariffType, tariff2: TariffType): boolean {
  const osds1 = TARIFF_DESCRIPTIONS[tariff1].availableOSDs;
  const osds2 = TARIFF_DESCRIPTIONS[tariff2].availableOSDs;

  if (osds1.length !== osds2.length) return false;

  return osds1.every((osd) => osds2.includes(osd));
}

// Export default for convenience
export default {
  TARIFF_DESCRIPTIONS,
  getTariffDescription,
  getTariffsForOSD,
  getTariffColor,
  getTariffIcon,
  getTariffBadge,
  getSmartMeterRequiredTariffs,
  requiresSmartMeter,
  requiresPriceData,
  getAllTariffCodes,
  getTariffsBySmartMeterRequirement,
  searchTariffs,
  getOSDExclusiveTariffs,
  getUniversalTariffs,
  haveSameOSDAvailability,
};
